/**
 * My Services Page (provider-facing)
 *
 * Two sections:
 *  1. Authorized services — read-only view of ProfileProvider.capable_services, i.e. what
 *     this provider actually receives dispatches for. Managed by a corporate manager or Ops.
 *  2. My proposals — services this provider has proposed, and where each one stands.
 *
 * Gated on holding a PROVIDER profile rather than on CASL: AbilityProvider prefers the
 * ENTITY/ORGANIZATION profile's rules, so a provider-granted rule can be discarded.
 */

import { useMemo, useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { ClipboardList, PlusCircle, ShieldCheck } from 'lucide-react'
import { ProposeServiceDialog } from '@/components/providers/ProposeServiceDialog'
import { useMyServiceProposals, useServiceProposalActions } from '@/hooks/useMyServiceProposals'
import { useMyAuthorizedServices } from '@/hooks/useMyAuthorizedServices'
import { useProviderProfile } from '@/hooks/useProviderProfile'
import { useLocalization } from '@/contexts/LocalizationContext'
import { formatCurrency, formatDate } from '@/utils/formatters'
import type { ServiceProposal, ServiceProposalStatus } from '@/types/providerServices'
import type { BadgeProps } from '@/components/ui/badge'

const STATUS_VARIANT: Record<ServiceProposalStatus, BadgeProps['variant']> = {
  pending: 'secondary',
  approved: 'default',
  rejected: 'destructive',
}

export function MyServicesPage() {
  const { t, locale } = useLocalization()
  const { isProvider, isLoading: isLoadingProfile } = useProviderProfile()

  const {
    services,
    isLoading: isLoadingServices,
    isError: isServicesError,
  } = useMyAuthorizedServices(isProvider)

  const {
    proposals,
    isLoading: isLoadingProposals,
    isError: isProposalsError,
    error: proposalsError,
  } = useMyServiceProposals(isProvider)

  const { submitProposal, isLoading: isSubmitting } = useServiceProposalActions()

  const [proposeOpen, setProposeOpen] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)

  const stats = useMemo(() => {
    const counts = { pending: 0, approved: 0, rejected: 0 }
    for (const p of proposals) {
      if (p.status in counts) counts[p.status as keyof typeof counts] += 1
    }
    return counts
  }, [proposals])

  // Wait for the profile list before deciding — otherwise a provider sees a flash of
  // "provider account required" while the me query is still in flight.
  if (isLoadingProfile) {
    return (
      <div className="container mx-auto space-y-4 px-4 py-4 sm:py-6">
        <Skeleton className="h-8 w-56" />
        <Skeleton className="h-40 w-full" />
      </div>
    )
  }

  if (!isProvider) {
    return (
      <div className="container mx-auto px-4 py-10">
        <Card className="mx-auto max-w-md text-center">
          <CardHeader>
            <CardTitle>{t('myServices.notAProviderTitle')}</CardTitle>
          </CardHeader>
          <CardContent className="text-sm text-muted-foreground">
            {t('myServices.notAProviderBody')}
          </CardContent>
        </Card>
      </div>
    )
  }

  const statusLabel = (p: ServiceProposal) => {
    const key = `myServices.status.${p.status}`
    const translated = t(key)
    return translated === key ? p.statusDisplay || p.status : translated
  }

  return (
    <div className="container mx-auto space-y-4 px-4 py-4 sm:space-y-6 sm:py-6">
      {/* ── Header ─────────────────────────────────────────────────── */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-semibold">{t('myServices.title')}</h1>
          <p className="text-sm text-muted-foreground">{t('myServices.subtitle')}</p>
        </div>
        <Button
          onClick={() => {
            setSubmitError(null)
            setProposeOpen(true)
          }}
          className="bg-gradient-to-r from-brand-primary to-brand-secondary text-white"
        >
          <PlusCircle className="h-4 w-4 me-2" />
          {t('myServices.proposeService')}
        </Button>
      </div>

      {/* ── Authorized services ────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="h-4 w-4 text-muted-foreground" />
            {t('myServices.authorizedServices')}
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            {t('myServices.authorizedServicesHint')}
          </p>
        </CardHeader>
        <CardContent>
          {isLoadingServices ? (
            <div className="flex flex-wrap gap-2">
              {Array.from({ length: 4 }).map((_, i) => (
                <Skeleton key={i} className="h-6 w-28" />
              ))}
            </div>
          ) : isServicesError ? (
            <p className="text-sm text-muted-foreground">
              {t('providerServices.loadFailed')}
            </p>
          ) : services.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {t('providerServices.noAuthorizedServices')}
            </p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {services.map((s) => (
                <Badge key={s.id} variant="outline" className="gap-1.5 py-1 text-xs">
                  <span>{s.label || s.name}</span>
                  {s.basePrice != null && (
                    <span className="text-muted-foreground">
                      {formatCurrency(Number(s.basePrice), locale)}
                    </span>
                  )}
                  {s.estimatedDuration != null && (
                    <span className="text-muted-foreground">
                      {t('providerServices.minutesShort', { count: s.estimatedDuration })}
                    </span>
                  )}
                </Badge>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* ── Proposals ──────────────────────────────────────────────── */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <ClipboardList className="h-4 w-4 text-muted-foreground" />
            {t('myServices.myProposals')}
          </CardTitle>
          {proposals.length > 0 && (
            <div className="flex flex-wrap gap-2 pt-1">
              <Badge variant="secondary" className="text-xs">
                {t('myServices.pendingCount', { count: stats.pending })}
              </Badge>
              <Badge variant="default" className="text-xs">
                {t('myServices.approvedCount', { count: stats.approved })}
              </Badge>
              <Badge variant="destructive" className="text-xs">
                {t('myServices.rejectedCount', { count: stats.rejected })}
              </Badge>
            </div>
          )}
        </CardHeader>
        <CardContent>
          {isProposalsError ? (
            <Card className="border-red-200 bg-red-50">
              <CardContent className="p-4 text-sm text-red-700">
                {t('myServices.loadFailed')}
                {': '}
                {(proposalsError as Error)?.message || t('common.unknownError')}
              </CardContent>
            </Card>
          ) : isLoadingProposals ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : proposals.length === 0 ? (
            <div className="py-8 text-center">
              <p className="text-sm text-muted-foreground">{t('myServices.noProposals')}</p>
              <Button
                variant="outline"
                className="mt-3"
                onClick={() => {
                  setSubmitError(null)
                  setProposeOpen(true)
                }}
              >
                {t('myServices.proposeFirst')}
              </Button>
            </div>
          ) : (
            <>
              {/* Mobile cards */}
              <div className="space-y-3 md:hidden">
                {proposals.map((p) => (
                  <div key={p.id} className="rounded-lg border p-3">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-medium">{p.serviceName}</div>
                        <div className="text-xs text-muted-foreground">
                          {p.categoryName || t('providerServices.uncategorized')}
                        </div>
                      </div>
                      <Badge variant={STATUS_VARIANT[p.status] ?? 'secondary'} className="text-xs">
                        {statusLabel(p)}
                      </Badge>
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-muted-foreground">
                      {p.proposedPrice != null && (
                        <span>{formatCurrency(Number(p.proposedPrice), locale)}</span>
                      )}
                      <span>{formatDate(p.createdAt, locale)}</span>
                    </div>
                    {p.status === 'rejected' && p.rejectionReason && (
                      <p className="mt-2 text-xs text-red-600">
                        {t('myServices.rejectionReason')}: {p.rejectionReason}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Desktop table */}
              <div className="hidden overflow-x-auto md:block">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('myServices.serviceName')}</TableHead>
                      <TableHead>{t('myServices.category')}</TableHead>
                      <TableHead>{t('myServices.proposedPrice')}</TableHead>
                      <TableHead>{t('myServices.submittedOn')}</TableHead>
                      <TableHead>{t('common.status')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {proposals.map((p) => (
                      <TableRow key={p.id}>
                        <TableCell className="font-medium">
                          <div className="min-w-0">
                            <div className="truncate">{p.serviceName}</div>
                            {p.status === 'rejected' && p.rejectionReason && (
                              <div className="mt-0.5 text-xs text-red-600">
                                {t('myServices.rejectionReason')}: {p.rejectionReason}
                              </div>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {p.categoryName || '—'}
                        </TableCell>
                        <TableCell>
                          {p.proposedPrice != null
                            ? formatCurrency(Number(p.proposedPrice), locale)
                            : '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {formatDate(p.createdAt, locale)}
                        </TableCell>
                        <TableCell>
                          <Badge variant={STATUS_VARIANT[p.status] ?? 'secondary'}>
                            {statusLabel(p)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      <ProposeServiceDialog
        open={proposeOpen}
        onOpenChange={setProposeOpen}
        isLoading={isSubmitting}
        submitError={submitError}
        onSubmit={async (input) => {
          setSubmitError(null)
          try {
            await submitProposal(input)
          } catch (err) {
            // Keep the dialog open and show the reason inline; the hook already toasted.
            setSubmitError((err as Error)?.message ?? t('myServices.submitFailed'))
            throw err
          }
        }}
      />
    </div>
  )
}
